import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { type ReportRange, reportApi, reportKeys } from '../api';

/**
 * Đường cong S và Earned Value — spec 16.3.
 *
 * Ba đường, ba câu hỏi: PV = *lẽ ra* đã xong bao nhiêu, EV = **đã** xong bao
 * nhiêu, AC = đã bỏ ra bao nhiêu. SPI và CPI chỉ là tỉ số giữa chúng, nên khung
 * này hiện luôn hai chỉ số ở phụ đề — đó là hai con số lãnh đạo hỏi trước khi
 * nhìn hình.
 *
 * ⚠️ Đơn vị là **giờ**, không phải tiền, và ghi chú dưới biểu đồ nói rõ AC lấy
 * ở đâu ra. Một biểu đồ EVM không nói đơn vị của nó sẽ được đọc như báo cáo tài
 * chính, và ở đây thì không có đồng nào là thật.
 */
export function EvmChart({
  projectId,
  range,
}: {
  projectId: string;
  range: ReportRange;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.evm(projectId, range),
    queryFn: () => reportApi.evm(projectId, range),
  });

  const buckets = report.data?.buckets ?? [];
  const hasIndex = report.data?.spi != null && report.data?.cpi != null;

  return (
    <ChartFrame
      title={t('reports.evmTitle')}
      help="evm"
      subtitle={
        hasIndex
          ? t('reports.evmSubtitle', { spi: report.data!.spi, cpi: report.data!.cpi })
          : t('reports.evmNoIndex')
      }
      asOf={range.asOf ? new Date(range.asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        // BAC > 0 mà cả ba đường vẫn phẳng ở 0 nghĩa là task **có** ước lượng
        // nhưng chưa có ngày kế hoạch — PV không tính được. Vẽ một trục rỗng ở
        // đó trông như lỗi tải dữ liệu; câu hướng dẫn nói đúng thứ còn thiếu.
        report.data &&
        buckets.every((bucket) => bucket.pv === 0 && bucket.ev === 0 && bucket.ac === 0)
          ? { title: t('reports.evmEmpty'), description: t('reports.evmEmptyHelp') }
          : null
      }
      legend={[
        { label: t('reports.pv'), color: RAG_VAR.unknown },
        { label: t('reports.ev'), color: FLOW_VAR.done },
        { label: t('reports.ac'), color: RAG_VAR.amber },
      ]}
    >
      <div className="flex flex-col gap-1">
        <div className="h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
              <YAxis tick={{ fontSize: 11 }} stroke="var(--color-border)" />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  fontSize: 12,
                }}
              />
              {/*
                PV vẽ dưới cùng dạng vùng mờ: nó là *cái nền* để so, còn EV và AC
                mới là hai đường phải đọc chồng lên nó.
              */}
              <Area
                type="monotone"
                dataKey="pv"
                name={t('reports.pv')}
                stroke={RAG_VAR.unknown}
                fill={RAG_VAR.unknown}
                fillOpacity={0.15}
              />
              <Area
                type="monotone"
                dataKey="ev"
                name={t('reports.ev')}
                stroke={FLOW_VAR.done}
                fill={FLOW_VAR.done}
                fillOpacity={0.2}
              />
              <Area
                type="monotone"
                dataKey="ac"
                name={t('reports.ac')}
                stroke={RAG_VAR.amber}
                fill="transparent"
                strokeDasharray="4 4"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <p className="text-xs text-muted-foreground">{t('reports.evmUnitNote')}</p>
        {(report.data?.unestimatedTaskCount ?? 0) > 0 && (
          // Một dự án nửa chưa ước lượng mà BAC vẫn trông đầy đủ là cách nhanh
          // nhất để lãnh đạo tin vào một con số không có nguồn.
          <p className="text-xs text-rag-amber">
            {t('reports.evmUnestimated', { count: report.data!.unestimatedTaskCount })}
          </p>
        )}
      </div>
    </ChartFrame>
  );
}
